import { ExportableCanvas } from './ExportableCanvas';
import { getDevicePixelRatio } from './canvas';
import {
  getCanvasScreenPoint,
  getCanvasWorldPoint,
  getWheelZoomFactor,
} from './viewport';
import type { Point } from '../types';
import type { ViewportContextBase, ViewportPoint, ViewportSnapshot } from './ViewportControllerBase';
import type { Shortcut } from '../input/shortcuts';
import { handleShortcutActions } from '../input/shortcuts';
import { ViewportControllerBase } from './ViewportControllerBase';

export abstract class ViewportCanvasBase<
  PngOptions extends object,
  DataUrlOptions extends object
> extends ExportableCanvas<PngOptions, DataUrlOptions> {
  protected abstract canvas: HTMLCanvasElement;
  protected abstract viewport: ViewportControllerBase<ViewportContextBase>;
  protected onChangeCallback: (() => void) | null = null;
  protected spacePressed = false;
  protected isPanning = false;
  protected panStart: Point | null = null;
  protected resizeObserver: ResizeObserver | null = null;
  protected firstResizeHandled = false;
  protected abstract getPanModeCursor(enabled: boolean): string;

  setOnViewChange(callback: (scale: number) => void): void {
    this.viewport.setOnViewChange(callback);
  }

  protected get scale(): number {
    return this.viewport.getScale();
  }

  protected set scale(value: number) {
    this.viewport.setScale(value);
  }

  protected get panX(): number {
    return this.viewport.getPan().panX;
  }

  protected set panX(value: number) {
    this.viewport.setPan(value, this.panY);
  }

  protected get panY(): number {
    return this.viewport.getPan().panY;
  }

  protected set panY(value: number) {
    this.viewport.setPan(this.panX, value);
  }

  protected get gridEnabled(): boolean {
    return this.viewport.isGridEnabled();
  }

  protected set gridEnabled(value: boolean) {
    this.viewport.applyState({ gridEnabled: value });
  }

  protected get snapEnabled(): boolean {
    return this.viewport.isSnapEnabled();
  }

  protected set snapEnabled(value: boolean) {
    this.viewport.applyState({ snapEnabled: value });
  }

  protected get gridSize(): number {
    return this.viewport.getGridSize();
  }

  protected set gridSize(value: number) {
    this.viewport.applyState({ gridSize: value });
  }

  protected get inverted(): boolean {
    return this.viewport.isInverted();
  }

  protected set inverted(value: boolean) {
    this.viewport.applyState({ inverted: value });
  }

  setOnChange(callback: () => void): void {
    this.onChangeCallback = callback;
  }

  getScale(): number {
    return this.viewport.getScale();
  }

  zoomIn(): void {
    const center = this.getCanvasCenter();
    this.viewport.zoomAtPoint(center, 1.1);
  }

  zoomOut(): void {
    const center = this.getCanvasCenter();
    this.viewport.zoomAtPoint(center, 1 / 1.1);
  }

  protected getCanvasCenter(): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: rect.width / 2, y: rect.height / 2 };
  }

  setOnToggleChange(callback: (grid: boolean, snap: boolean, invert: boolean) => void): void {
    this.viewport.setOnToggleChange(callback);
  }

  setGridEnabled(enabled: boolean): void {
    this.viewport.setGridEnabled(enabled);
  }

  isGridEnabled(): boolean {
    return this.viewport.isGridEnabled();
  }

  setSnapEnabled(enabled: boolean): void {
    this.viewport.setSnapEnabled(enabled);
  }

  isSnapEnabled(): boolean {
    return this.viewport.isSnapEnabled();
  }

  protected get panModeEnabled(): boolean {
    return this.viewport.isPanModeEnabled();
  }

  protected set panModeEnabled(value: boolean) {
    this.viewport.setPanModeEnabled(value);
  }

  setPanMode(enabled: boolean): void {
    this.panModeEnabled = enabled;
    this.canvas.style.cursor = this.getPanModeCursor(enabled);
  }

  getPanMode(): boolean {
    return this.panModeEnabled;
  }

  setInverted(enabled: boolean): void {
    this.viewport.setInverted(enabled);
  }

  isInverted(): boolean {
    return this.viewport.isInverted();
  }

  protected snapToGrid<T extends ViewportPoint>(point: T): T {
    return this.viewport.snapToGrid(point);
  }

  protected getWorldPoint(event: MouseEvent | PointerEvent): Point {
    return getCanvasWorldPoint(this.canvas, event, {
      panX: this.panX,
      panY: this.panY,
      scale: this.scale,
    });
  }

  protected getViewportSnapshot(): ViewportSnapshot {
    return {
      ...this.viewport.getPanScale(),
      inverted: this.viewport.isInverted(),
      gridEnabled: this.viewport.isGridEnabled(),
      gridSize: this.viewport.getGridSize(),
    };
  }

  protected initCanvasBase(cursor = 'crosshair'): void {
    this.canvas.style.touchAction = 'none';
    this.canvas.style.cursor = cursor;
  }

  protected initResizeObserver(
    onResize: (width: number, height: number, dpr: number) => void
  ): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const dpr = getDevicePixelRatio();
        onResize(width, height, dpr);
      }
    });

    const container = this.canvas.parentElement;
    if (container) {
      this.resizeObserver.observe(container);
    }
  }

  protected centerOnFirstResize(width: number, height: number, allow = true): void {
    if (this.firstResizeHandled) return;
    if (allow) {
      this.panX = width / 2;
      this.panY = height / 2;
    }
    this.firstResizeHandled = true;
  }

  protected startPan(event: PointerEvent): void {
    this.canvas.setPointerCapture(event.pointerId);
    this.isPanning = true;
    this.panStart = getCanvasScreenPoint(this.canvas, event);
    this.canvas.style.cursor = 'grabbing';
  }

  protected updatePan(event: PointerEvent): void {
    if (!this.panStart) return;
    const screenPoint = getCanvasScreenPoint(this.canvas, event);
    this.panX += screenPoint.x - this.panStart.x;
    this.panY += screenPoint.y - this.panStart.y;
    this.panStart = screenPoint;
  }

  protected stopPan(): void {
    this.isPanning = false;
    this.panStart = null;
    this.canvas.style.cursor = this.getPanModeCursor(this.spacePressed || this.panModeEnabled);
  }

  protected shouldStartPan(event: PointerEvent): boolean {
    return event.button === 1 || (event.button === 0 && (this.spacePressed || this.panModeEnabled));
  }

  protected startPanHold(): void {
    this.spacePressed = true;
    this.canvas.style.cursor = this.getPanModeCursor(true);
  }

  protected endPanHold(cursorEnabled: boolean = this.panModeEnabled): void {
    this.spacePressed = false;
    if (!this.isPanning) {
      this.canvas.style.cursor = this.getPanModeCursor(cursorEnabled);
    }
  }

  protected handlePanZoomShortcuts(
    e: KeyboardEvent,
    shortcuts: { panHold: Shortcut; zoomReset: Shortcut; zoomIn: Shortcut; zoomOut: Shortcut },
    isMac: boolean,
    options: { onReset?: () => void } = {}
  ): boolean {
    const canStartPanHold = (): boolean => !this.spacePressed;
    const handlePanHold = (): void => this.startPanHold();
    const handleZoomReset = (): void => options.onReset?.();
    const handleZoomIn = (): void => this.zoomIn();
    const handleZoomOut = (): void => this.zoomOut();

    return handleShortcutActions(
      e,
      [
        {
          shortcut: shortcuts.panHold,
          preventDefault: true,
          when: canStartPanHold,
          action: handlePanHold,
        },
        {
          shortcut: shortcuts.zoomReset,
          preventDefault: true,
          action: handleZoomReset,
        },
        { shortcut: shortcuts.zoomIn, preventDefault: true, action: handleZoomIn },
        { shortcut: shortcuts.zoomOut, preventDefault: true, action: handleZoomOut },
      ],
      isMac
    );
  }

  protected handlePanHoldKeyUp(e: KeyboardEvent, cursorEnabled?: boolean): void {
    if (e.key === ' ') {
      this.endPanHold(cursorEnabled);
    }
  }

  protected handleViewportWheel(e: WheelEvent, onAfter?: () => void): void {
    e.preventDefault();

    const zoomFactor = getWheelZoomFactor(e);
    if (zoomFactor) {
      const screenPoint = getCanvasScreenPoint(this.canvas, e);
      this.viewport.zoomAtPoint(screenPoint, zoomFactor);
    } else {
      // Pan (trackpad scroll)
      this.viewport.panBy(-e.deltaX, -e.deltaY);
    }

    onAfter?.();
  }

  protected createPanKeyUpHandler(cursorEnabled?: boolean): (e: KeyboardEvent) => void {
    return (e: KeyboardEvent): void => {
      this.handlePanHoldKeyUp(e, cursorEnabled);
    };
  }

  protected createWheelHandler(onAfter?: () => void): (e: WheelEvent) => void {
    return (e: WheelEvent): void => {
      this.handleViewportWheel(e, onAfter);
    };
  }
}
